import { fetchLatestGameNews, type GameNewsLocales } from './api'

export interface GameNewsWarm {
  /** ISO timestamp the news row was created (used as the event start date). */
  createdAt?: string
  locales?: GameNewsLocales
}

let cachedNews: GameNewsWarm | undefined
let fetchedAt = 0
let inflight: Promise<GameNewsWarm | undefined> | null = null

const DEFAULT_TTL_MS = 120_000

/**
 * Returns the latest news (locales + createdAt), using a short-lived cache and deduping concurrent requests.
 * Used by NewsModal so the shell opens immediately while data is often already warm.
 */
export function getGameNews(maxAgeMs: number = DEFAULT_TTL_MS): Promise<GameNewsWarm | undefined> {
  const age = Date.now() - fetchedAt
  if (fetchedAt > 0 && age < maxAgeMs && !inflight) {
    return Promise.resolve(cachedNews)
  }
  if (inflight) return inflight

  inflight = fetchLatestGameNews()
    .then((res) => {
      cachedNews = res.success && res.news ? { createdAt: res.news.createdAt, locales: res.news.locales } : undefined
      fetchedAt = Date.now()
      return cachedNews
    })
    .catch(() => {
      fetchedAt = Date.now()
      return cachedNews
    })
    .finally(() => {
      inflight = null
    })
  return inflight
}

/** Latest news locales only (back-compat helper). */
export function getGameNewsLocales(maxAgeMs: number = DEFAULT_TTL_MS): Promise<GameNewsLocales | undefined> {
  return getGameNews(maxAgeMs).then((n) => n?.locales)
}

/** Fire-and-forget prefetch (e.g. after app boot) so the first modal open is instant. */
export function prefetchGameNews(): void {
  void getGameNews()
}
